import React, { useState, useEffect, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  getKeywordNameAPI,
  getAdKeywordRankHistoryAPI,
  updateKeywordRankAPI,
  updateCoupangKeywordRankAPI,
} from "../../../../../api/index";
import { useRecoilValue } from "recoil";
import { userState } from "../../../../../recoil/auth";
import LoadingOverlay from "../../../../../components/common/LoadingOverlay";
import "./RankCheckModal.css";

const RankCheckModal = ({
  isOpen,
  onClose,
  adData,
  onRefresh,
  missionType,
  missionTemplate,
  isMobile = false,
}) => {
  const user = useRecoilValue(userState);
  
  // missionType과 missionTemplate을 props 또는 adData에서 가져오기
  const effectiveMissionType = missionType || adData?.missionType || adData?.ms_id || 1;
  const effectiveMissionTemplate = missionTemplate || adData?.missionTemplate || adData?.adType || 1;
  const [selectedAdName, setSelectedAdName] = useState("");
  const [chartData, setChartData] = useState([]); // Recharts는 배열 형식
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [overlayMessage, setOverlayMessage] = useState(null);
  const [yAxisRange, setYAxisRange] = useState({ min: 1, max: 100 });
  const [sortedRankHistory, setSortedRankHistory] = useState([]);
  const [mainKeywordId, setMainKeywordId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const isRankDataLoading = useRef(false);
  const dotPositions = useRef({}); // 각 점의 실제 X 좌표 저장
  const [activeIndex, setActiveIndex] = useState(-1); // 현재 활성화된 점의 인덱스
  const [mobileHeaderHeight, setMobileHeaderHeight] = useState(0); // 모바일 헤더 높이

  // 모바일에서 실제 뷰포트 높이 측정하여 헤더 높이 계산
  useEffect(() => {
    if (isMobile && isOpen) {
      const calculateHeaderHeight = () => {
        // iOS 감지 (Safari, Chrome 모두 포함)
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        
        // 실제 헤더 요소가 렌더링되었는지 확인
        const headerElement = document.querySelector('.rank-check-modal-mobile-header');
        
        // HomeMobile 헤더가 숨겨졌으므로, 헤더는 top: 0부터 시작
        // iOS 주소창 높이만 고려
        let headerTop = 0;
        
        if (isIOS) {
          // iOS 노치가 있는 기기 감지
          const hasNotch = window.screen.height >= 812 || window.screen.width >= 812;
          const safeAreaTop = hasNotch ? 44 : 0;
          
          // iOS Safari/Chrome 주소창 높이 계산
          let addressBarHeight = 0;
          if (window.visualViewport) {
            try {
              const visualHeight = window.visualViewport.height;
              const windowHeight = window.innerHeight;
              const calculatedHeight = Math.max(0, windowHeight - visualHeight);
              // 측정값이 합리적인 범위 내에 있으면 사용 (0-100px)
              if (calculatedHeight >= 0 && calculatedHeight <= 100) {
                addressBarHeight = calculatedHeight;
              } else {
                // 측정값이 비정상적이면 기본값 사용
                addressBarHeight = 44;
              }
            } catch (e) {
              addressBarHeight = 44;
            }
          } else {
            // visualViewport가 없으면 보수적인 기본값 사용
            addressBarHeight = 44;
          }
          
          // iOS 주소창 높이만 고려 (safe-area + 주소창)
          headerTop = safeAreaTop + addressBarHeight;
        }
        
        const headerHeight = 56;
        const extraPadding = isIOS ? 20 : 10;
        const totalHeight = headerTop + headerHeight + extraPadding;
        
        // 실제 헤더 요소가 렌더링되었는지 확인하여 재측정
        if (headerElement) {
          const rect = headerElement.getBoundingClientRect();
          const actualHeaderTop = rect.top;
          const actualHeaderHeight = rect.height || 56;
          
          // 실제 측정값이 있으면 사용 (더 정확함)
          // 단, 측정값이 0보다 작거나 비정상적으로 크면 기본값 사용
          if (actualHeaderTop >= 0 && actualHeaderTop < 200) {
            const extraPadding = isIOS ? 20 : 10;
            const totalHeight = actualHeaderTop + actualHeaderHeight + extraPadding;
            
            setMobileHeaderHeight(totalHeight);
            
            document.documentElement.style.setProperty(
              "--rank-check-mobile-header-height",
              `${totalHeight}px`
            );
            document.documentElement.style.setProperty(
              "--rank-check-mobile-header-top",
              `${actualHeaderTop}px`
            );
            return;
          }
        }
        
        setMobileHeaderHeight(totalHeight);
        
        document.documentElement.style.setProperty(
          "--rank-check-mobile-header-height",
          `${totalHeight}px`
        );
        document.documentElement.style.setProperty(
          "--rank-check-mobile-header-top",
          `${headerTop}px`
        );
      };

      // 즉시 계산
      calculateHeaderHeight();
      
      // 리사이즈 이벤트 리스너 추가
      window.addEventListener("resize", calculateHeaderHeight);
      window.addEventListener("orientationchange", calculateHeaderHeight);
      if (window.visualViewport) {
        window.visualViewport.addEventListener("resize", calculateHeaderHeight);
      }
      
      // 여러 시점에서 재계산 (렌더링 지연 대응)
      const timeouts = [
        setTimeout(calculateHeaderHeight, 50),
        setTimeout(calculateHeaderHeight, 100),
        setTimeout(calculateHeaderHeight, 200),
        setTimeout(calculateHeaderHeight, 300),
        setTimeout(calculateHeaderHeight, 500),
        setTimeout(calculateHeaderHeight, 1000),
      ];
      
      return () => {
        window.removeEventListener("resize", calculateHeaderHeight);
        window.removeEventListener("orientationchange", calculateHeaderHeight);
        if (window.visualViewport) {
          window.visualViewport.removeEventListener("resize", calculateHeaderHeight);
        }
        timeouts.forEach(id => clearTimeout(id));
      };
    }
  }, [isMobile, isOpen]);

  // 모바일에서 RankCheckModal이 열릴 때 body에 클래스 추가하여 HomeMobile 헤더 숨기기
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.classList.add('rank-check-modal-open');
      return () => {
        document.body.classList.remove('rank-check-modal-open');
      };
    }
  }, [isMobile, isOpen]);

  // Y축 동적 계산 함수
  const calculateYAxisRange = (rankData) => {
    if (rankData.length === 1) {
      const singleRank = rankData[0];
      if (singleRank === 500) {
        return { min: 450, max: 500, stepSize: 10 };
      } else {
        const margin = Math.max(10, Math.ceil(singleRank * 0.2));
        const min = Math.max(1, singleRank - margin);
        const max = singleRank + margin;
        const stepSize = Math.max(1, Math.ceil((max - min) / 5));
        return { min, max, stepSize };
      }
    }

    const maxRank = Math.max(...rankData);
    const minRank = Math.min(...rankData);
    const dataRange = maxRank - minRank;

    if (dataRange === 0) {
      const margin = Math.max(10, Math.ceil(maxRank * 0.2));
      const min = Math.max(1, maxRank - margin);
      const max = maxRank + margin;
      const stepSize = Math.max(1, Math.ceil((max - min) / 5));
      return { min, max, stepSize };
    }

    let yMin, yMax, stepSize;

    // 데이터의 실제 최소값과 최대값을 Y축 범위로 사용 (마진 없음)
    yMin = Math.max(1, minRank); // 최소값은 1 이상
    yMax = maxRank;

    // stepSize 계산
    const range = yMax - yMin;
    if (range <= 10) {
      stepSize = 1;
    } else if (range <= 30) {
      stepSize = Math.max(1, Math.ceil(range / 5));
    } else if (range <= 50) {
      stepSize = Math.max(1, Math.ceil(range / 6));
    } else if (range <= 100) {
      stepSize = Math.max(1, Math.ceil(range / 7));
    } else {
      stepSize = Math.max(1, Math.ceil(range / 8));
    }

    if (maxRank === 500) {
      yMax = 500;
      stepSize = 10;
    }

    return { min: yMin, max: yMax, stepSize };
  };

  // 시간 순서(길이) 기준 색상 함수 (처음부터: 초록, 노랑, 주황, 빨강)
  const getColorByIndex = (index, totalLength) => {
    if (totalLength <= 1) return '#10b981';
    
    // 인덱스 비율 계산 (0부터 1까지)
    const indexRatio = index / (totalLength - 1);
    
    // 인덱스 비율에 따라 색상 결정 (처음부터: 초록, 노랑, 주황, 빨강)
    if (indexRatio < 0.25) {
      // 처음 25%: 초록
      return '#10b981';
    } else if (indexRatio < 0.5) {
      // 25-50%: 노랑
      return '#fbbf24';
    } else if (indexRatio < 0.75) {
      // 50-75%: 주황
      return '#f97316';
    } else {
      // 75-100%: 빨강
      return '#ef4444';
    }
  };

  // 차트 데이터 업데이트 함수
  const updateChartData = (adRankHistory) => {
    // 500+위(rank가 999)를 제외하고 날짜별 순위 데이터 정리
    let filteredData = adRankHistory.filter((item) => item.rank !== 999); // 500+위 제외
    
    if (filteredData.length === 0) {
      setSortedRankHistory([]);
      return;
    }
    
    // 날짜순으로 정렬 (오래된 것부터)
    const sortedByDate = filteredData.sort((a, b) => new Date(a.date) - new Date(b.date));
    
    // 처음과 끝 데이터 추출
    const firstItem = sortedByDate[0];
    const lastItem = sortedByDate[sortedByDate.length - 1];
    
    let sortedRankHistory;
    
    if (sortedByDate.length <= 10) {
      // 10개 이하면 모두 포함
      sortedRankHistory = sortedByDate;
    } else {
      // 10개 초과면 처음과 끝을 포함하고, 변화가 큰 구간 우선 선택
      const middleItems = sortedByDate.slice(1, -1); // 처음과 끝 제외한 중간 데이터
      const middleCount = 8; // 처음과 끝 제외하고 8개
      
      // 각 데이터 포인트의 변화량 계산 (이전 순위와의 차이)
      // 순위가 낮아질수록(숫자가 작아질수록) 좋은 것이므로, 변화량 = 이전 순위 - 현재 순위
      const itemsWithChange = middleItems.map((item, index) => {
        const prevIndex = sortedByDate.findIndex(d => d === item) - 1;
        const prevItem = prevIndex >= 0 ? sortedByDate[prevIndex] : null;
        const change = prevItem ? prevItem.rank - item.rank : 0; // 양수면 개선(순위 상승), 음수면 하락
        return { item, change, index: sortedByDate.findIndex(d => d === item) };
      });
      
      // 변화량이 큰 순서대로 정렬 (개선이 큰 것 우선)
      itemsWithChange.sort((a, b) => b.change - a.change);
      
      // 상위 8개 선택
      const selectedItems = itemsWithChange.slice(0, middleCount).map(i => i.item);
      
      // 선택된 항목들을 날짜순으로 정렬
      const allSelected = [firstItem, ...selectedItems, lastItem];
      sortedRankHistory = allSelected.sort((a, b) => new Date(a.date) - new Date(b.date));
    }

    setSortedRankHistory(sortedRankHistory);

    if (sortedRankHistory.length === 0) {
      return;
    }

    const ranks = sortedRankHistory.map((item) => item.rank);
    const minRank = Math.min(...ranks);
    const maxRank = Math.max(...ranks);

    // Y축 동적 계산
    const yAxisRange = calculateYAxisRange(ranks);
    setYAxisRange(yAxisRange);

    // Recharts 형식으로 데이터 변환 (시간 순서 기준으로 색상 계산)
    const rechartsData = sortedRankHistory.map((item, index) => ({
      name: `${item.rank}위`,
      rank: item.rank,
      date: item.date,
      color: getColorByIndex(index, sortedRankHistory.length),
      index: index, // 인덱스 추가
    }));

    setChartData(rechartsData);
  };

  // 모달이 열릴 때 이전 데이터 초기화
  useEffect(() => {
    if (isOpen && adData) {
      // 모달이 열릴 때 이전 데이터 초기화
      setChartData([]);
      setSortedRankHistory([]);
      setYAxisRange({ min: 1, max: 100 });
      setError(null);
      setOverlayMessage(null);
      setLoading(false);
      dotPositions.current = {}; // 점 위치 초기화
      setActiveIndex(-1); // 활성 인덱스 초기화
    } else if (!isOpen) {
      // 모달이 닫힐 때도 초기화
      setChartData([]);
      setSortedRankHistory([]);
      setYAxisRange({ min: 1, max: 100 });
      setError(null);
      setOverlayMessage(null);
      setLoading(false);
      dotPositions.current = {}; // 점 위치 초기화
      setActiveIndex(-1); // 활성 인덱스 초기화
    }
  }, [isOpen, adData]);

  // 광고 데이터가 변경될 때마다 순위 데이터 로딩
  useEffect(() => {
    const loadRankData = async () => {
      if (!isOpen || !adData) {
        return;
      }

      if (isRankDataLoading.current) {
        return;
      }

      isRankDataLoading.current = true;
      setLoading(true);
      setError(null);
      setOverlayMessage(null);

      try {
        // 로그인 상태 확인
        if (!user?.id) {
          setOverlayMessage("로그인이 필요합니다.");
          return;
        }

        // 1. 키워드 이름 조회
        const adId = adData.adId || adData.ad_id;
        const keywordResponse = await getKeywordNameAPI(adId);
        const keywords = keywordResponse.data?.data || [];

        // MAIN 타입 키워드 찾기
        const mainKeyword = keywords.find((keyword) => keyword.type === "MAIN");

        if (!mainKeyword) {
          setOverlayMessage("메인 키워드를 찾을 수 없습니다.");
          return;
        }

        setSelectedAdName(mainKeyword.name);
        setMainKeywordId(mainKeyword.keyword_id);

        // 2. 광고 ID로 키워드 순위 히스토리 조회
        const adRankResponse = await getAdKeywordRankHistoryAPI(adId);
        let adRankHistory = adRankResponse.data?.data || [];

        // 날짜 필터링: 리셋일과 시작일 중 더 이른 날짜부터 현재 또는 종료일 중 더 작은 값까지
        if (adRankHistory.length > 0) {
          // 날짜 문자열 추출 헬퍼 함수
          const extractDateStr = (dateValue) => {
            if (!dateValue) return null;
            
            if (typeof dateValue === 'string') {
              if (dateValue.includes('T')) {
                return dateValue.split('T')[0];
              } else if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                return dateValue;
              } else {
                const tempDate = new Date(dateValue);
                return tempDate.toISOString().split('T')[0];
              }
            } else if (dateValue instanceof Date) {
              return dateValue.toISOString().split('T')[0];
            } else {
              const tempDate = new Date(dateValue);
              return tempDate.toISOString().split('T')[0];
            }
          };

          // 리셋일과 시작일 추출
          const lastResetDateStr = adData?.lastResetDate ? extractDateStr(adData.lastResetDate) : null;
          const startDateStr = adData?.startDate ? extractDateStr(adData.startDate) : null;
          
          // 필터링 시작일: lastResetDate와 startDate 중 더 이른 날짜 사용
          // 단, startDate가 더 이전이면 startDate 기준 7일 전부터
          let filterStartDateStr;
          if (lastResetDateStr && startDateStr) {
            if (startDateStr < lastResetDateStr) {
              // startDate가 더 이전이면 startDate 기준 7일 전
              const startDateObj = new Date(startDateStr + 'T00:00:00.000Z');
              startDateObj.setDate(startDateObj.getDate() - 7);
              filterStartDateStr = startDateObj.toISOString().split('T')[0];
            } else {
              // lastResetDate가 더 이전이면 그것 사용
              filterStartDateStr = lastResetDateStr;
            }
          } else if (lastResetDateStr) {
            // lastResetDate만 있으면 그것 사용
            filterStartDateStr = lastResetDateStr;
          } else if (startDateStr) {
            // startDate만 있으면 startDate 기준 7일 전
            const startDateObj = new Date(startDateStr + 'T00:00:00.000Z');
            startDateObj.setDate(startDateObj.getDate() - 7);
            filterStartDateStr = startDateObj.toISOString().split('T')[0];
          } else {
            // 둘 다 없으면 필터링하지 않음
            filterStartDateStr = null;
          }
          
          // 필터링 시작일이 없으면 필터링하지 않음
          if (!filterStartDateStr) {
            // 차트 데이터 업데이트 (필터링 없이)
            updateChartData(adRankHistory);
            return;
          }
          
          // 종료일 날짜 문자열 추출 (YYYY-MM-DD)
          const endDateStr = adData?.endDate ? extractDateStr(adData.endDate) : null;

          // 현재 날짜 문자열 (KST 기준) - UTC+9시간
          const now = new Date();
          const currentDateStr = now.toISOString().split('T')[0];

          // 필터링 기준 종료일: 종료일이 있고 종료일이 현재보다 이전이면 종료일, 아니면 현재 날짜
          const filterEndDateStr = endDateStr && endDateStr < currentDateStr ? endDateStr : currentDateStr;

          // 날짜 필터링 적용 (날짜 문자열로 비교)
          const beforeFilterCount = adRankHistory.length;
          adRankHistory = adRankHistory.filter((item) => {
            const itemDate = new Date(item.date);
            // UTC 날짜를 KST로 변환하여 날짜 문자열 추출 (UTC + 9시간)
            const itemDateStr = itemDate.toISOString().split('T')[0];
            return itemDateStr >= filterStartDateStr && itemDateStr <= filterEndDateStr;
          });
          
          console.log('날짜 필터링:', {
            lastResetDateStr,
            startDateStr,
            filterStartDateStr,
            endDateStr,
            currentDateStr,
            filterEndDateStr,
            beforeFilterCount,
            afterFilterCount: adRankHistory.length,
            filteredData: adRankHistory.map(item => {
              const itemDate = new Date(item.date);
              return {
                date: item.date,
                dateStr: itemDate.toISOString().split('T')[0],
                rank: item.rank
              };
            })
          });
        }

        // 차트 데이터 업데이트
        updateChartData(adRankHistory);
      } catch (error) {
        console.error("순위 데이터 로딩 오류:", error);
        setError("순위 데이터를 불러오는 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
        isRankDataLoading.current = false;
      }
    };

    loadRankData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, adData, user?.id]);

  // 새로고침 핸들러
  const handleRefresh = async () => {
    if (!mainKeywordId || refreshing) {
      return;
    }

    setRefreshing(true);
    setError(null);
    setOverlayMessage(null);

    try {
      let updatedRankHistory = [];

      if (effectiveMissionTemplate === 13) {
        // 쿠팡: 쿠팡 순위 업데이트 API 호출 후 히스토리 재조회
        await updateCoupangKeywordRankAPI(mainKeywordId);
        const adId = adData?.ad_id || adData?.id;
        if (adId) {
          const adRankResponse = await getAdKeywordRankHistoryAPI(adId);
          updatedRankHistory = adRankResponse.data?.data || [];
        }
      } else {
        // 네이버 쇼핑: 순위 업데이트 API 호출 (응답이 히스토리)
        const updateResponse = await updateKeywordRankAPI(mainKeywordId);
        updatedRankHistory = updateResponse.data?.data || [];
      }

      // 날짜 필터링: 리셋일과 시작일 중 더 이른 날짜부터 현재 또는 종료일 중 더 작은 값까지
      if (updatedRankHistory.length > 0) {
        // 날짜 문자열 추출 헬퍼 함수
        const extractDateStr = (dateValue) => {
          if (!dateValue) return null;
          
          if (typeof dateValue === 'string') {
            if (dateValue.includes('T')) {
              return dateValue.split('T')[0];
            } else if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
              return dateValue;
            } else {
              const tempDate = new Date(dateValue);
              return tempDate.toISOString().split('T')[0];
            }
          } else if (dateValue instanceof Date) {
            return dateValue.toISOString().split('T')[0];
          } else {
            const tempDate = new Date(dateValue);
            return tempDate.toISOString().split('T')[0];
          }
        };

        // 리셋일과 시작일 추출
        const lastResetDateStr = adData?.lastResetDate ? extractDateStr(adData.lastResetDate) : null;
        const startDateStr = adData?.startDate ? extractDateStr(adData.startDate) : null;
        
        // 필터링 시작일: lastResetDate와 startDate 중 더 이른 날짜 사용
        // 단, startDate가 더 이전이면 startDate 기준 7일 전부터
        let filterStartDateStr;
        if (lastResetDateStr && startDateStr) {
          if (startDateStr < lastResetDateStr) {
            // startDate가 더 이전이면 startDate 기준 7일 전
            const startDateObj = new Date(startDateStr + 'T00:00:00.000Z');
            startDateObj.setDate(startDateObj.getDate() - 7);
            filterStartDateStr = startDateObj.toISOString().split('T')[0];
          } else {
            // lastResetDate가 더 이전이면 그것 사용
            filterStartDateStr = lastResetDateStr;
          }
        } else if (lastResetDateStr) {
          // lastResetDate만 있으면 그것 사용
          filterStartDateStr = lastResetDateStr;
        } else if (startDateStr) {
          // startDate만 있으면 startDate 기준 7일 전
          const startDateObj = new Date(startDateStr + 'T00:00:00.000Z');
          startDateObj.setDate(startDateObj.getDate() - 7);
          filterStartDateStr = startDateObj.toISOString().split('T')[0];
        } else {
          // 둘 다 없으면 필터링하지 않음
          filterStartDateStr = null;
        }

        // 필터링 시작일이 있으면 필터링 적용
        if (filterStartDateStr) {
          // 종료일 날짜 문자열 추출 (YYYY-MM-DD)
          const endDateStr = adData?.endDate ? extractDateStr(adData.endDate) : null;

          // 현재 날짜 문자열 (KST 기준) - UTC+9시간
          const now = new Date();
          const currentDateStr = now.toISOString().split('T')[0];

          // 필터링 기준 종료일: 종료일이 있고 종료일이 현재보다 이전이면 종료일, 아니면 현재 날짜
          const filterEndDateStr = endDateStr && endDateStr < currentDateStr ? endDateStr : currentDateStr;

          // 날짜 필터링 적용 (날짜 문자열로 비교)
          updatedRankHistory = updatedRankHistory.filter((item) => {
            const itemDate = new Date(item.date);
            // UTC 날짜를 KST로 변환하여 날짜 문자열 추출 (UTC + 9시간)
            const itemDateStr = itemDate.toISOString().split('T')[0];
            return itemDateStr >= filterStartDateStr && itemDateStr <= filterEndDateStr;
          });
        }
      }

      if (updatedRankHistory.length === 0) {
        return;
      }

      // 차트 데이터 업데이트
      updateChartData(updatedRankHistory);

      // 부모 컴포넌트에 새로고침 완료 알림
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("순위 새로고침 오류:", error);
      setError("순위를 새로고침하는 중 오류가 발생했습니다.");
    } finally {
      setRefreshing(false);
    }
  };

  if (!isOpen) return null;

  // 모바일일 때는 페이지 형태로 렌더링
  if (isMobile) {
    return (
      <div className="rank-check-modal-mobile-page">
        <LoadingOverlay
          isVisible={refreshing}
          message="순위 새로고침 중..."
        />
        <div className="rank-check-modal-mobile-content">
        {/* 헤더 */}
        <div className="rank-check-modal-mobile-header">
          <button
            className="rank-check-modal-mobile-back-btn"
            onClick={onClose}
          >
            ← 뒤로
          </button>
          <h2>{adData?.adName || adData?.missionName || "순위체크"}</h2>
          <div className="rank-check-modal-mobile-header-actions">
            {/* missionType: 1, missionTemplate: 1 또는 2(쇼핑) 또는 13(쿠팡)일 때 새로고침 버튼 표시 */}
            {
              (effectiveMissionTemplate === 1 ||
                effectiveMissionTemplate === 2 ||
                effectiveMissionTemplate === 13) && (
                <button
                  className="rank-check-modal-popup-refresh-btn"
                  onClick={handleRefresh}
                  disabled={!mainKeywordId || refreshing || loading}
                  title="순위 새로고침"
                >
                  {refreshing ? "새로고침 중..." : "새로고침"}
                </button>
              )}
          </div>
        </div>

        {/* 메인 컨텐츠: 좌우 2패널 레이아웃 */}
        <div className="rank-check-modal-popup-content">
          {/* 좌측 패널: 제품 정보 및 성능 */}
          <div className="rank-check-modal-popup-left-panel">
            {/* 제품 이미지 */}
            <div className="rank-check-modal-popup-product-image-container">
            <img
              src={adData?.image || adData?.imageUrl || "/logo192.png"}
              alt={adData?.adName || adData?.missionName || "광고 이미지"}
                className="rank-check-modal-popup-product-image"
              onError={(e) => {
                e.target.src = "/logo192.png";
              }}
            />
            </div>

            {/* PROGRESS 섹션 */}
            {/* <div className="rank-check-modal-popup-progress-section">
              <div className="rank-check-modal-popup-progress-label">
                PROGRESS
              </div>
              {(() => {
                const dailyQuantity = parseInt(adData?.dailyQuantity) || 0;
                
                if (!dailyQuantity) {
                  return (
                    <>
                      <div className="rank-check-modal-popup-progress-text">
                        오늘 유입현황 0% 달성
                      </div>
                      <div className="rank-check-modal-popup-progress-bar">
                        <div
                          className="rank-check-modal-popup-progress-bar-fill"
                          style={{ width: "0%" }}
                        ></div>
                      </div>
                      <div className="rank-check-modal-popup-inflow-details">
                        <span className="rank-check-modal-popup-remaining-inflow">
                          잔여유입 0
                        </span>
                        <span className="rank-check-modal-popup-paid-inflow">
                          0 결제유입
                        </span>
                      </div>
                    </>
                  );
                }
                
                // 현재 한국 시간 가져오기
                const now = new Date();
                const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
                const koreaNow = new Date(utcTime + (9 * 60 * 60 * 1000));
                const currentHour = koreaNow.getHours(); // 0-23
                
                // 00시부터 22시까지 기준으로 계산 (22시간)
                const maxHour = 22;
                const minHour = 0;
                
                // 현재 시간이 22시 이후면 100%, 0시 이전이면 0%
                let progressPercentage = 0;
                let remaining = dailyQuantity;
                
                if (currentHour >= maxHour) {
                  // 22시 이후면 100% 달성
                  progressPercentage = 100;
                  remaining = 0;
                } else if (currentHour >= minHour) {
                  // 0시~22시 사이면 비례 계산
                  // 진행률 = (현재시간 / 22) * 100
                  progressPercentage = Math.min(100, Math.round((currentHour / maxHour) * 100));
                  
                  // 잔여유입 = dailyQuantity * ((22 - 현재시간) / 22)
                  remaining = Math.max(0, Math.floor(dailyQuantity * ((maxHour - currentHour) / maxHour)));
                }
                
                return (
                  <>
                    <div className="rank-check-modal-popup-progress-text">
                      오늘 유입현황 {progressPercentage}% 달성
                    </div>
                    <div className="rank-check-modal-popup-progress-bar">
                      <div
                        className="rank-check-modal-popup-progress-bar-fill"
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                    <div className="rank-check-modal-popup-inflow-details">
                      <span className="rank-check-modal-popup-remaining-inflow">
                        잔여유입 {remaining.toLocaleString()}
              </span>
                      <span className="rank-check-modal-popup-paid-inflow">
                        {dailyQuantity.toLocaleString()} 결제유입
              </span>
                    </div>
                  </>
                );
              })()}
            </div> */}

            {/* 브랜드 로고 */}
            {adData?.company && (
              <div className="rank-check-modal-popup-brand-logo">
                {adData.company}
              </div>
            )}

            {/* 광고 종료일 */}
            {adData?.endDate && (
              <div className="rank-check-modal-popup-end-date">
                <div className="rank-check-modal-popup-end-date-label">
                  광고 종료일
                </div>
                <div className="rank-check-modal-popup-end-date-value">
                  {(() => {
                    try {
                      const date = new Date(adData.endDate);
                      const month = (date.getMonth() + 1)
                        .toString()
                        .padStart(2, "0");
                      const day = date.getDate().toString().padStart(2, "0");
                      return `${month}월 ${day}일`;
                    } catch {
                      return adData.endDate;
                    }
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* 우측 패널: 순위 히스토리 */}
          <div className="rank-check-modal-popup-right-panel">
            {/* 최근순위와 차트 통합 컨테이너 */}
            <div className="rank-check-modal-popup-chart-container">
              최근순위
              {sortedRankHistory.length > 0 && (
                <div className="rank-check-modal-popup-recent-rank">
                  <div className="rank-check-modal-popup-recent-rank-header">
          </div>
                  <div className="rank-check-modal-popup-recent-rank-value">
                    {(() => {
                      const latestRank = sortedRankHistory[sortedRankHistory.length - 1]?.rank;
                      if (latestRank === 999) return "500+위";
                      return `${latestRank}위`;
                    })()}
          </div>
        </div>
              )}

              {/* 차트 */}
              <div className="rank-check-modal-popup-chart-wrapper">
          {loading ? (
            <div className="rank-check-modal-popup-loading-message">
              로딩 중...
            </div>
          ) : error ? (
                  <div className="rank-check-modal-popup-error-message">
                    {error}
                  </div>
                ) : chartData && chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={chartData}
                      margin={{ top: 10, right: 10, left: 5, bottom: 0 }}
                      syncId="rankChart"
                    >
                      <defs>
                        {/* 그라데이션 정의 */}
                        <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.05} />
                        </linearGradient>
                        {/* 점 그림자 필터 */}
                        <filter id="dotShadow" x="-50%" y="-50%" width="200%" height="200%">
                          <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
                          <feOffset dx="0" dy="2" result="offsetblur" />
                          <feComponentTransfer>
                            <feFuncA type="linear" slope="0.3" />
                          </feComponentTransfer>
                          <feMerge>
                            <feMergeNode />
                            <feMergeNode in="SourceGraphic" />
                          </feMerge>
                        </filter>
                      </defs>
                      <CartesianGrid 
                        strokeDasharray="3 3" 
                        stroke="#e5e7eb" 
                        strokeOpacity={0.5}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="name"
                        tick={{ 
                          fill: '#6b7280', 
                          fontSize: 13, 
                          fontWeight: 500,
                          fontFamily: 'system-ui, -apple-system, sans-serif'
                        }}
                        angle={45}
                        textAnchor="start"
                        height={120}
                        interval={0}
                        axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                        tickLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                      />
                      <YAxis
                        domain={[yAxisRange.min, yAxisRange.max]}
                        reversed
                        hide
                        allowDataOverflow={false}
                        allowDecimals={false}
                        padding={{ top: 25, bottom: 25 }}
                      />
                      <Tooltip
                        content={({ active, payload, coordinate }) => {
                          if (!active || !payload || !payload.length) {
                            setActiveIndex(-1);
                            return null;
                          }
                          
                          let data = null;
                          
                          // 저장된 실제 X 좌표를 사용하여 가장 가까운 데이터 포인트 찾기
                          // X축 거리만 고려하여 같은 순위의 점들 중에서도 정확한 점 선택
                          if (coordinate && chartData.length > 0 && Object.keys(dotPositions.current).length > 0) {
                            let closestIndex = -1;
                            let minDistance = Infinity;
                            
                            // 모든 점의 실제 X 좌표와 비교
                            chartData.forEach((item, index) => {
                              const dotX = dotPositions.current[index];
                              if (dotX !== undefined && !isNaN(dotX)) {
                                const distance = Math.abs(dotX - coordinate.x);
                                if (distance < minDistance) {
                                  minDistance = distance;
                                  closestIndex = index;
                                }
                              }
                            });
                            
                            // 가장 가까운 인덱스의 데이터 사용
                            if (closestIndex >= 0 && closestIndex < chartData.length) {
                              data = chartData[closestIndex];
                              // 활성 인덱스 업데이트
                              setActiveIndex(closestIndex);
                            }
                          }
                          
                          // fallback: payload에서 가져오기 (하지만 같은 순위일 때는 부정확할 수 있음)
                          if (!data && payload && payload.length > 0) {
                            data = payload[0].payload;
                          }
                          
                          if (!data) {
                            setActiveIndex(-1);
                            return null;
                          }
                          
                          const utcDate = new Date(data.date);
                          
                          return (
                            <div
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                color: '#111827',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                border: '1px solid #e5e7eb',
                                fontSize: '13px',
                                fontFamily: 'system-ui, -apple-system, sans-serif',
                                pointerEvents: 'none',
                              }}
                            >
                              <div style={{ 
                                fontWeight: 600, 
                                marginBottom: '6px',
                                color: '#374151',
                                fontSize: '12px'
                              }}>
                                {utcDate.toLocaleDateString("ko-KR", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </div>
                              <div style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <span style={{
                                  display: 'inline-block',
                                  width: '10px',
                                  height: '10px',
                                  borderRadius: '50%',
                                  backgroundColor: data.color || '#3b82f6'
                                }}></span>
                                <span style={{ fontWeight: 600, color: '#111827' }}>
                                  순위: <span style={{ color: data.color || '#3b82f6' }}>{data.rank}위</span>
                                </span>
                              </div>
                            </div>
                          );
                        }}
                        cursor={{ stroke: '#d1d5db', strokeWidth: 1, strokeDasharray: '5 5' }}
                        allowEscapeViewBox={{ x: false, y: false }}
                        onMouseLeave={() => setActiveIndex(-1)}
                        wrapperStyle={{ transition: 'all 0.2s ease' }}
                      />
                      {/* 하나의 연속된 선 */}
                      <Line
                        type="monotone"
                        dataKey="rank"
                        stroke="#3b82f6"
                        strokeWidth={8}
                        dot={(props) => {
                          const { cx, cy, payload, index } = props;
                          if (!payload || cx === undefined || cy === undefined) return null;
                          
                          // 실제 X 좌표 저장 (payload.index 우선, 없으면 index 사용)
                          const dataIndex = payload.index !== undefined ? payload.index : (index !== undefined ? index : -1);
                          if (dataIndex >= 0 && cx !== undefined) {
                            dotPositions.current[dataIndex] = cx;
                          }
                          
                          // 활성화된 점인지 확인
                          const isActive = activeIndex === dataIndex;
                          
                          return (
                            <g>
                              {/* 호버 영역 확대를 위한 보이지 않는 큰 원 */}
                              <circle
                                cx={cx}
                                cy={cy}
                                r={15}
                                fill="transparent"
                                style={{ cursor: 'pointer' }}
                                data-index={dataIndex}
                              />
                              {/* 활성화된 점의 큰 원 (activeDot 대신) */}
                              {isActive && (
                                <circle
                                  cx={cx}
                                  cy={cy}
                                  r={12}
                                  fill={payload.color || '#3b82f6'}
                                  stroke="#fff"
                                  strokeWidth={3}
                                  filter="url(#dotShadow)"
                                  style={{ transition: 'all 0.2s ease', pointerEvents: 'none' }}
                                />
                              )}
                              {/* 실제 보이는 점 */}
                              <circle
                                cx={cx}
                                cy={cy}
                                r={isActive ? 0 : 7}
                                fill={payload.color || '#3b82f6'}
                                stroke="#fff"
                                strokeWidth={2.5}
                                filter="url(#dotShadow)"
                                style={{ transition: 'all 0.2s ease', pointerEvents: 'none' }}
                              />
                            </g>
                          );
                        }}
                        activeDot={false}
                        connectNulls={true}
                        isAnimationActive={true}
                        animationDuration={800}
                        animationEasing="ease-out"
                      />
                    </LineChart>
                  </ResponsiveContainer>
          ) : (
            <div className="rank-check-modal-popup-no-data-message">
              차트 데이터가 없습니다.
            </div>
                )
                }
              </div>
            </div>

            {/* 순위 히스토리 테이블 */}
            {sortedRankHistory.length > 0 && (
              <div className="rank-check-modal-popup-rank-history-table">
                <table>
                  <thead>
                    <tr>
                      <th>시간</th>
                      <th>순위</th>
                      <th>조회키워드</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...sortedRankHistory].reverse().map((item, index) => {
                      const utcDate = new Date(item.date);
                      const month = (utcDate.getMonth() + 1)
                        .toString()
                        .padStart(2, "0");
                      const day = utcDate.getDate().toString().padStart(2, "0");
                      const hours = utcDate.getHours().toString().padStart(2, "0");
                      const minutes = utcDate
                        .getMinutes()
                        .toString()
                        .padStart(2, "0");
                      const timeString = `${month}. ${day}. ${hours}시${minutes}분`;
                      const displayRank =
                        item.rank === 999 ? "500+" : item.rank;

                      return (
                        <tr key={index}>
                          <td>{timeString}</td>
                          <td>{displayRank}</td>
                          <td>{item.keyword || item.keywordName || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
            </div>
          )}
          </div>
        </div>

        {/* 오버레이 메시지 */}
        {overlayMessage && (
          <div className="rank-check-modal-popup-overlay-message">
            <div className="rank-check-modal-popup-overlay-content">
              {overlayMessage}
            </div>
          </div>
        )}
        </div>
      </div>
    );
  }

  // 데스크톱 모달 형태로 렌더링
  return (
    <div className="rank-check-modal-popup-overlay" onClick={onClose}>
      <LoadingOverlay
        isVisible={refreshing}
        message="순위 새로고침 중..."
      />
      <div
        className="rank-check-modal-popup"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 */}
        <div className="rank-check-modal-popup-header">
          <h2>{adData?.adName || adData?.missionName || "순위체크"}</h2>
          <div className="rank-check-modal-popup-header-actions">
            {/* missionType: 1, missionTemplate: 1 또는 2(쇼핑) 또는 13(쿠팡)일 때 새로고침 버튼 표시 */}
            {
              (effectiveMissionTemplate === 1 ||
                effectiveMissionTemplate === 2 ||
                effectiveMissionTemplate === 13) && (
                <button
                  className="rank-check-modal-popup-refresh-btn"
                  onClick={handleRefresh}
                  disabled={!mainKeywordId || refreshing || loading}
                  title="순위 새로고침"
                >
                  {refreshing ? "새로고침 중..." : "새로고침"}
                </button>
              )}
            <button
              className="rank-check-modal-popup-close-btn"
              onClick={onClose}
            ></button>
          </div>
        </div>

        {/* 메인 컨텐츠: 좌우 2패널 레이아웃 */}
        <div className="rank-check-modal-popup-content">
          {/* 좌측 패널: 제품 정보 및 성능 */}
          <div className="rank-check-modal-popup-left-panel">
            {/* 제품 이미지 */}
            <div className="rank-check-modal-popup-product-image-container">
            <img
              src={adData?.image || adData?.imageUrl || "/logo192.png"}
              alt={adData?.adName || adData?.missionName || "광고 이미지"}
                className="rank-check-modal-popup-product-image"
              onError={(e) => {
                e.target.src = "/logo192.png";
              }}
            />
            </div>

            {/* PROGRESS 섹션 */}
            {/* <div className="rank-check-modal-popup-progress-section">
              <div className="rank-check-modal-popup-progress-label">
                PROGRESS
              </div>
              {(() => {
                const dailyQuantity = parseInt(adData?.dailyQuantity) || 0;
                
                if (!dailyQuantity) {
                  return (
                    <>
                      <div className="rank-check-modal-popup-progress-text">
                        오늘 유입현황 0% 달성
                      </div>
                      <div className="rank-check-modal-popup-progress-bar">
                        <div
                          className="rank-check-modal-popup-progress-bar-fill"
                          style={{ width: "0%" }}
                        ></div>
                      </div>
                      <div className="rank-check-modal-popup-inflow-details">
                        <span className="rank-check-modal-popup-remaining-inflow">
                          잔여유입 0
                        </span>
                        <span className="rank-check-modal-popup-paid-inflow">
                          0 결제유입
                        </span>
                      </div>
                    </>
                  );
                }
                
                // 현재 한국 시간 가져오기
                const now = new Date();
                const utcTime = now.getTime() + (now.getTimezoneOffset() * 60 * 1000);
                const koreaNow = new Date(utcTime + (9 * 60 * 60 * 1000));
                const currentHour = koreaNow.getHours(); // 0-23
                
                // 00시부터 22시까지 기준으로 계산 (22시간)
                const maxHour = 22;
                const minHour = 0;
                
                // 현재 시간이 22시 이후면 100%, 0시 이전이면 0%
                let progressPercentage = 0;
                let remaining = dailyQuantity;
                
                if (currentHour >= maxHour) {
                  // 22시 이후면 100% 달성
                  progressPercentage = 100;
                  remaining = 0;
                } else if (currentHour >= minHour) {
                  // 0시~22시 사이면 비례 계산
                  // 진행률 = (현재시간 / 22) * 100
                  progressPercentage = Math.min(100, Math.round((currentHour / maxHour) * 100));
                  
                  // 잔여유입 = dailyQuantity * ((22 - 현재시간) / 22)
                  remaining = Math.max(0, Math.floor(dailyQuantity * ((maxHour - currentHour) / maxHour)));
                }
                
                return (
                  <>
                    <div className="rank-check-modal-popup-progress-text">
                      오늘 유입현황 {progressPercentage}% 달성
                    </div>
                    <div className="rank-check-modal-popup-progress-bar">
                      <div
                        className="rank-check-modal-popup-progress-bar-fill"
                        style={{ width: `${progressPercentage}%` }}
                      ></div>
                    </div>
                    <div className="rank-check-modal-popup-inflow-details">
                      <span className="rank-check-modal-popup-remaining-inflow">
                        잔여유입 {remaining.toLocaleString()}
              </span>
                      <span className="rank-check-modal-popup-paid-inflow">
                        {dailyQuantity.toLocaleString()} 결제유입
              </span>
                    </div>
                  </>
                );
              })()}
            </div> */}

            {/* 브랜드 로고 */}
            {adData?.company && (
              <div className="rank-check-modal-popup-brand-logo">
                {adData.company}
              </div>
            )}

            {/* 광고 종료일 */}
            {adData?.endDate && (
              <div className="rank-check-modal-popup-end-date">
                <div className="rank-check-modal-popup-end-date-label">
                  광고 종료일
                </div>
                <div className="rank-check-modal-popup-end-date-value">
                  {(() => {
                    try {
                      const date = new Date(adData.endDate);
                      const month = (date.getMonth() + 1)
                        .toString()
                        .padStart(2, "0");
                      const day = date.getDate().toString().padStart(2, "0");
                      return `${month}월 ${day}일`;
                    } catch {
                      return adData.endDate;
                    }
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* 우측 패널: 순위 히스토리 */}
          <div className="rank-check-modal-popup-right-panel">
            {/* 최근순위와 차트 통합 컨테이너 */}
            <div className="rank-check-modal-popup-chart-container">
              최근순위
              {sortedRankHistory.length > 0 && (
                <div className="rank-check-modal-popup-recent-rank">
                  <div className="rank-check-modal-popup-recent-rank-header">
          </div>
                  <div className="rank-check-modal-popup-recent-rank-value">
                    {(() => {
                      const latestRank = sortedRankHistory[sortedRankHistory.length - 1]?.rank;
                      if (latestRank === 999) return "500+위";
                      return `${latestRank}위`;
                    })()}
          </div>
        </div>
              )}

              {/* 차트 */}
              <div className="rank-check-modal-popup-chart-wrapper">
          {loading ? (
            <div className="rank-check-modal-popup-loading-message">
              로딩 중...
            </div>
          ) : error ? (
                  <div className="rank-check-modal-popup-error-message">
                    {error}
                  </div>
                ) : chartData && chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart
                      data={chartData}
                      margin={{ top: 10, right: 10, left: 5, bottom: 0 }}
                      syncId="rankChart"
                    >
                      <defs>
                        {/* 그라데이션 정의 */}
                        <linearGradient id="lineGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.05} />
                        </linearGradient>
                        {/* 점 그림자 필터 */}
                        <filter id="dotShadow" x="-50%" y="-50%" width="200%" height="200%">
                          <feGaussianBlur in="SourceAlpha" stdDeviation="2" />
                          <feOffset dx="0" dy="2" result="offsetblur" />
                          <feComponentTransfer>
                            <feFuncA type="linear" slope="0.3" />
                          </feComponentTransfer>
                          <feMerge>
                            <feMergeNode />
                            <feMergeNode in="SourceGraphic" />
                          </feMerge>
                        </filter>
                      </defs>
                      <CartesianGrid 
                        strokeDasharray="3 3" 
                        stroke="#e5e7eb" 
                        strokeOpacity={0.5}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="name"
                        tick={{ 
                          fill: '#6b7280', 
                          fontSize: 13, 
                          fontWeight: 500,
                          fontFamily: 'system-ui, -apple-system, sans-serif'
                        }}
                        angle={45}
                        textAnchor="start"
                        height={120}
                        interval={0}
                        axisLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                        tickLine={{ stroke: '#d1d5db', strokeWidth: 1 }}
                      />
                      <YAxis
                        domain={[yAxisRange.min, yAxisRange.max]}
                        reversed
                        hide
                        allowDataOverflow={false}
                        allowDecimals={false}
                        padding={{ top: 25, bottom: 25 }}
                      />
                      <Tooltip
                        content={({ active, payload, coordinate }) => {
                          if (!active || !payload || !payload.length) {
                            setActiveIndex(-1);
                            return null;
                          }
                          
                          let data = null;
                          
                          // 저장된 실제 X 좌표를 사용하여 가장 가까운 데이터 포인트 찾기
                          // X축 거리만 고려하여 같은 순위의 점들 중에서도 정확한 점 선택
                          if (coordinate && chartData.length > 0 && Object.keys(dotPositions.current).length > 0) {
                            let closestIndex = -1;
                            let minDistance = Infinity;
                            
                            // 모든 점의 실제 X 좌표와 비교
                            chartData.forEach((item, index) => {
                              const dotX = dotPositions.current[index];
                              if (dotX !== undefined && !isNaN(dotX)) {
                                const distance = Math.abs(dotX - coordinate.x);
                                if (distance < minDistance) {
                                  minDistance = distance;
                                  closestIndex = index;
                                }
                              }
                            });
                            
                            // 가장 가까운 인덱스의 데이터 사용
                            if (closestIndex >= 0 && closestIndex < chartData.length) {
                              data = chartData[closestIndex];
                              // 활성 인덱스 업데이트
                              setActiveIndex(closestIndex);
                            }
                          }
                          
                          // fallback: payload에서 가져오기 (하지만 같은 순위일 때는 부정확할 수 있음)
                          if (!data && payload && payload.length > 0) {
                            data = payload[0].payload;
                          }
                          
                          if (!data) {
                            setActiveIndex(-1);
                            return null;
                          }
                          
                          const utcDate = new Date(data.date);
                          
                          return (
                            <div
                              style={{
                                backgroundColor: 'rgba(255, 255, 255, 0.98)',
                                padding: '12px 16px',
                                borderRadius: '8px',
                                color: '#111827',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                                border: '1px solid #e5e7eb',
                                fontSize: '13px',
                                fontFamily: 'system-ui, -apple-system, sans-serif',
                                pointerEvents: 'none',
                              }}
                            >
                              <div style={{ 
                                fontWeight: 600, 
                                marginBottom: '6px',
                                color: '#374151',
                                fontSize: '12px'
                              }}>
                                {utcDate.toLocaleDateString("ko-KR", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </div>
                              <div style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                              }}>
                                <span style={{
                                  display: 'inline-block',
                                  width: '10px',
                                  height: '10px',
                                  borderRadius: '50%',
                                  backgroundColor: data.color || '#3b82f6'
                                }}></span>
                                <span style={{ fontWeight: 600, color: '#111827' }}>
                                  순위: <span style={{ color: data.color || '#3b82f6' }}>{data.rank}위</span>
                                </span>
                              </div>
                            </div>
                          );
                        }}
                        cursor={{ stroke: '#d1d5db', strokeWidth: 1, strokeDasharray: '5 5' }}
                        allowEscapeViewBox={{ x: false, y: false }}
                        onMouseLeave={() => setActiveIndex(-1)}
                        wrapperStyle={{ transition: 'all 0.2s ease' }}
                      />
                      {/* 하나의 연속된 선 */}
                      <Line
                        type="monotone"
                        dataKey="rank"
                        stroke="#3b82f6"
                        strokeWidth={8}
                        dot={(props) => {
                          const { cx, cy, payload, index } = props;
                          if (!payload || cx === undefined || cy === undefined) return null;
                          
                          // 실제 X 좌표 저장 (payload.index 우선, 없으면 index 사용)
                          const dataIndex = payload.index !== undefined ? payload.index : (index !== undefined ? index : -1);
                          if (dataIndex >= 0 && cx !== undefined) {
                            dotPositions.current[dataIndex] = cx;
                          }
                          
                          // 활성화된 점인지 확인
                          const isActive = activeIndex === dataIndex;
                          
                          return (
                            <g>
                              {/* 호버 영역 확대를 위한 보이지 않는 큰 원 */}
                              <circle
                                cx={cx}
                                cy={cy}
                                r={15}
                                fill="transparent"
                                style={{ cursor: 'pointer' }}
                                data-index={dataIndex}
                              />
                              {/* 활성화된 점의 큰 원 (activeDot 대신) */}
                              {isActive && (
                                <circle
                                  cx={cx}
                                  cy={cy}
                                  r={12}
                                  fill={payload.color || '#3b82f6'}
                                  stroke="#fff"
                                  strokeWidth={3}
                                  filter="url(#dotShadow)"
                                  style={{ transition: 'all 0.2s ease', pointerEvents: 'none' }}
                                />
                              )}
                              {/* 실제 보이는 점 */}
                              <circle
                                cx={cx}
                                cy={cy}
                                r={isActive ? 0 : 7}
                                fill={payload.color || '#3b82f6'}
                                stroke="#fff"
                                strokeWidth={2.5}
                                filter="url(#dotShadow)"
                                style={{ transition: 'all 0.2s ease', pointerEvents: 'none' }}
                              />
                            </g>
                          );
                        }}
                        activeDot={false}
                        connectNulls={true}
                        isAnimationActive={true}
                        animationDuration={800}
                        animationEasing="ease-out"
                      />
                    </LineChart>
                  </ResponsiveContainer>
          ) : (
            <div className="rank-check-modal-popup-no-data-message">
              차트 데이터가 없습니다.
            </div>
                )
                }
              </div>
            </div>

            {/* 순위 히스토리 테이블 */}
            {sortedRankHistory.length > 0 && (
              <div className="rank-check-modal-popup-rank-history-table">
                <table>
                  <thead>
                    <tr>
                      <th>시간</th>
                      <th>순위</th>
                      <th>조회키워드</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...sortedRankHistory].reverse().map((item, index) => {
                      const utcDate = new Date(item.date);
                      const month = (utcDate.getMonth() + 1)
                        .toString()
                        .padStart(2, "0");
                      const day = utcDate.getDate().toString().padStart(2, "0");
                      const hours = utcDate.getHours().toString().padStart(2, "0");
                      const minutes = utcDate
                        .getMinutes()
                        .toString()
                        .padStart(2, "0");
                      const timeString = `${month}. ${day}. ${hours}시${minutes}분`;
                      const displayRank =
                        item.rank === 999 ? "500+" : item.rank;

                      return (
                        <tr key={index}>
                          <td>{timeString}</td>
                          <td>{displayRank}</td>
                          <td>{item.keyword || item.keywordName || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
            </div>
          )}
          </div>
        </div>

        {/* 오버레이 메시지 */}
        {overlayMessage && (
          <div className="rank-check-modal-popup-overlay-message">
            <div className="rank-check-modal-popup-overlay-content">
              {overlayMessage}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RankCheckModal;
